import React, { useEffect, useRef, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../api/useApi";

const EditProfilePage = () => {
    const fileInputRef = useRef();
    const { user, setUser } = useAuth();
    const { authFetch } = useApi();

    const [loading, setLoading] = useState(true);
    const [avatar, setAvatar] = useState(null);
    const [formValues, setFormValues] = useState({
        email: "",
        firstName: "",
        lastName: "",
        whatsappPhone: "",
        phone: "",
    });

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success", // or "error"
    });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setFormValues(user);
                setAvatar(user.avatarUrl || null);
            } catch (err) {
                console.error("Failed to fetch profile", err);
            } finally {
                setLoading(false);
            }
        };

        if (user?.id) fetchProfile();
    }, [user]);

    const handleChange = (e) => {
        setFormValues((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleAvatarChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const preview = URL.createObjectURL(file);
        setAvatar(preview);

        const formData = new FormData();
        formData.append("avatar", file);

        try {
            const response = await authFetch(`/admin/user/${user.id}/update-avatar`, {
                method: "PUT",
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Avatar upload failed");
            }

            const updatedUser = await response.json();
            setUser(updatedUser);

            setSnackbar({
                open: true,
                message: "Avatar uploaded successfully",
                severity: "success",
            });
        } catch (error) {
            console.error("Error uploading avatar:", error);
            setSnackbar({
                open: true,
                message: "Avatar upload failed. Please try again.",
                severity: "error",
            });
        }
    };

    const handleSubmit = async () => {
        try {
            const response = await fetch(`/admin/user/update`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formValues),
            });

            if (!response.ok) {
                throw new Error("Profile update failed");
            }

            const user = await response.json();
            setUser(user)

            setSnackbar({
                open: true,
                message: "Profile updated successfully",
                severity: "success",
            });
        } catch (error) {
            console.error("Error updating profile:", error);
            setSnackbar({
                open: true,
                message: "Something went wrong. Please try again.",
                severity: "error",
            });
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box maxWidth="md" mx="auto" mt={4}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h6" mb={2}>
                    Edit Profile
                </Typography>

                {/* Avatar Upload */}
                <Box display="flex" justifyContent="center" mb={3}>
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleAvatarChange}
                    />
                    <Avatar
                        src={avatar || undefined}
                        sx={{ width: 80, height: 80, cursor: "pointer" }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {!avatar && <PhotoCameraIcon />}
                    </Avatar>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={formValues.firstName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={formValues.lastName}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Email"
                            name="email"
                            type="email"
                            value={formValues.email}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Phone (Including countrycode)"
                            name="phone"
                            value={formValues.phone}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="WhatsApp Phone (Including countrycode)"
                            name="whatsappPhone"
                            value={formValues.whatsappPhone}
                            onChange={handleChange}
                        />
                    </Grid>
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end">
                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                        Update Profile
                    </Button>
                </Box>
            </Paper>

            {/* Snackbar for feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <MuiAlert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default EditProfilePage;